import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalCloseButton,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { useStore } from "../contexts/StoreContext";
import PageShell from "../layout/PageShell";

const TITLE_MAX = 40;
const BODY_MAX = 5000;
const LANGS = [
  { id: "en", label: "English" },
  { id: "fr", label: "French" },
  { id: "cn", label: "Chinese" },
] as const;

type LangId = (typeof LANGS)[number]["id"];

type NoticeText = Partial<Record<LangId, string>>;

type StoreNotice = {
  id: string;
  storeId?: string;
  batchId?: string;
  status?: string;
  title?: NoticeText;
  body?: NoticeText;
  html?: NoticeText;
  time?: { toDate?: () => Date };
  createdBy?: string;
};

const emptyText = (): Record<LangId, string> => ({ en: "", fr: "", cn: "" });

const filled = (value: string) => value.trim().length > 0;

const languageFilled = (notice: StoreNotice, lang: LangId) =>
  filled(notice.title?.[lang] || "") ||
  filled(notice.body?.[lang] || "") ||
  filled(notice.html?.[lang] || "");

const languagesOf = (notice: StoreNotice) =>
  LANGS.filter((lang) => languageFilled(notice, lang.id)).map((lang) => lang.label);

const previewOf = (notice: StoreNotice) => {
  for (const lang of ["fr", "en", "cn"] as LangId[]) {
    const title = notice.title?.[lang]?.trim();
    const body = notice.body?.[lang]?.trim();
    const html = (notice.html?.[lang] || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (title || body || html) return title || body || html;
  }
  return "";
};

const whenOf = (notice: StoreNotice) => {
  const date = notice.time?.toDate?.();
  if (!date) return "";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
};

const NoticeBroadcast: React.FC = () => {
  const toast = useToast();
  const { currentUser } = useAuth();
  const { allStores } = useStore();
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [storeSearch, setStoreSearch] = useState("");
  const [titles, setTitles] = useState(emptyText);
  const [bodies, setBodies] = useState(emptyText);
  const [htmls, setHtmls] = useState(emptyText);
  const [langTab, setLangTab] = useState<LangId>("fr");
  const [contentTab, setContentTab] = useState<"html" | "text">("text");
  const [writeMode, setWriteMode] = useState("");
  const [notices, setNotices] = useState<StoreNotice[]>([]);
  const [busyId, setBusyId] = useState("");
  const [editingKey, setEditingKey] = useState("");

  const bgCard = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("#d5e4ea", "gray.600");
  const textSecondary = useColorModeValue("gray.600", "gray.300");

  const stores = useMemo(
    () => [...allStores].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [allStores]
  );

  useEffect(() => {
    const noticesQuery = query(collection(db, "storeNotices"), orderBy("time", "desc"));
    return onSnapshot(
      noticesQuery,
      (snapshot) => {
        setNotices(snapshot.docs.map((item) => ({ id: item.id, ...(item.data() as Omit<StoreNotice, "id">) })));
      },
      (error) => {
        toast({
          title: "Could not load notices",
          description: error.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    );
  }, [toast]);

  const allSelected = stores.length > 0 && selectedStoreIds.length === stores.length;

  const visibleStores = useMemo(() => {
    const term = storeSearch.trim().toLowerCase();
    if (!term) return stores;
    return stores.filter((store) => (store.name || store.id).toLowerCase().includes(term));
  }, [storeSearch, stores]);

  const visibleAllSelected =
    visibleStores.length > 0 && visibleStores.every((store) => selectedStoreIds.includes(store.id));

  const toggleAll = () => {
    setSelectedStoreIds(allSelected ? [] : stores.map((store) => store.id));
  };

  const toggleVisible = () => {
    const ids = visibleStores.map((store) => store.id);
    setSelectedStoreIds((current) => {
      if (ids.length > 0 && ids.every((id) => current.includes(id))) {
        return current.filter((id) => !ids.includes(id));
      }
      return [...new Set([...current, ...ids])];
    });
  };

  const toggleStore = (id: string) => {
    setSelectedStoreIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  const groups = useMemo(() => {
    const grouped = new Map<string, StoreNotice[]>();
    notices.forEach((notice) => {
      const key = notice.batchId || notice.id;
      const list = grouped.get(key) || [];
      list.push(notice);
      grouped.set(key, list);
    });
    return [...grouped.values()];
  }, [notices]);

  const audienceOf = (group: StoreNotice[]) => {
    if (group.some((notice) => notice.storeId === "*")) return "All stores";
    return group.length === 1 ? "1 store" : `${group.length} stores`;
  };

  const editingGroup = groups.find((group) => (group[0]?.batchId || group[0]?.id) === editingKey) || null;

  const beginEdit = (group: StoreNotice[]) => {
    const notice = group[0];
    const nextTitles = emptyText();
    const nextBodies = emptyText();
    const nextHtmls = emptyText();
    LANGS.forEach((lang) => {
      nextTitles[lang.id] = notice.title?.[lang.id] || "";
      nextBodies[lang.id] = notice.body?.[lang.id] || "";
      nextHtmls[lang.id] = notice.html?.[lang.id] || "";
    });
    setTitles(nextTitles);
    setBodies(nextBodies);
    setHtmls(nextHtmls);
    setLangTab("fr");
    setContentTab("text");
    if (group.some((item) => item.storeId === "*")) {
      setSelectedStoreIds(stores.map((store) => store.id));
    } else {
      setSelectedStoreIds(group.map((item) => item.storeId).filter((id): id is string => Boolean(id)));
    }
    setEditingKey(notice.batchId || notice.id);
  };

  const clearComposer = () => {
    setEditingKey("");
    setTitles(emptyText());
    setBodies(emptyText());
    setHtmls(emptyText());
    setLangTab("fr");
    setContentTab("text");
    setSelectedStoreIds([]);
  };

  const send = async (publish: boolean) => {
    if (selectedStoreIds.length === 0) {
      toast({ title: "Check at least one store", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    const title: NoticeText = {};
    const body: NoticeText = {};
    const html: NoticeText = {};
    LANGS.forEach((lang) => {
      const nextTitle = titles[lang.id].trim();
      const nextBody = bodies[lang.id].trim();
      const nextHtml = htmls[lang.id].trim();
      if (!nextTitle && !nextBody && !nextHtml) return;
      if (nextTitle) title[lang.id] = nextTitle;
      if (nextBody) body[lang.id] = nextBody;
      if (nextHtml) html[lang.id] = nextHtml;
    });
    if (Object.keys(title).length === 0 && Object.keys(body).length === 0 && Object.keys(html).length === 0) {
      toast({
        title: "Enter a notice in at least one language",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const live = editingGroup?.some((item) => item.status === "active") === true;
    const status = publish || live ? "active" : "saved";
    setWriteMode(publish ? "send" : "save");
    try {
      const batch = writeBatch(db);
      const targets = allSelected ? ["*"] : selectedStoreIds;
      const fields = {
        status,
        title,
        body,
        html,
        ...(publish || !live ? { time: serverTimestamp() } : {}),
      };
      if (editingGroup) {
        const batchId = editingGroup[0].batchId || editingGroup[0].id;
        const remaining = new Map(editingGroup.map((notice) => [notice.storeId || "", notice]));
        targets.forEach((storeId) => {
          const current = remaining.get(storeId);
          if (current) {
            remaining.delete(storeId);
            batch.update(doc(db, "storeNotices", current.id), {
              storeId,
              batchId,
              ...fields,
            });
            return;
          }
          batch.set(doc(collection(db, "storeNotices")), {
            storeId,
            batchId,
            ...fields,
            createdBy: currentUser?.email || "",
            time: serverTimestamp(),
          });
        });
        remaining.forEach((notice) => {
          batch.delete(doc(db, "storeNotices", notice.id));
        });
      } else {
        const batchId = crypto.randomUUID();
        targets.forEach((storeId) => {
          batch.set(doc(collection(db, "storeNotices")), {
            storeId,
            batchId,
            ...fields,
            createdBy: currentUser?.email || "",
            time: serverTimestamp(),
          });
        });
      }
      await batch.commit();
      clearComposer();
      toast({
        title: publish ? "Notice sent" : "Notice saved",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: publish ? "Could not send the notice" : "Could not save the notice",
        description: error?.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setWriteMode("");
    }
  };

  const setStatus = async (group: StoreNotice[], status: "active" | "disabled") => {
    const key = group[0]?.batchId || group[0]?.id || "";
    setBusyId(key);
    try {
      const batch = writeBatch(db);
      group.forEach((notice) => {
        batch.update(doc(db, "storeNotices", notice.id), { status });
      });
      await batch.commit();
    } catch (error: any) {
      toast({
        title: "Could not update the notice",
        description: error?.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setBusyId("");
    }
  };

  const remove = async (group: StoreNotice[]) => {
    const accepted = window.confirm("Delete this notice? Stores will no longer see it.");
    if (!accepted) return;
    const key = group[0]?.batchId || group[0]?.id || "";
    setBusyId(key);
    try {
      const batch = writeBatch(db);
      group.forEach((notice) => {
        batch.delete(doc(db, "storeNotices", notice.id));
      });
      await batch.commit();
      if ((group[0]?.batchId || group[0]?.id) === editingKey) clearComposer();
    } catch (error: any) {
      toast({
        title: "Could not delete the notice",
        description: error?.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setBusyId("");
    }
  };

  return (
    <PageShell
      title="Notices"
      description="Choose the stores that should receive this notice. A language left blank is not sent."
      wide
    >
      <VStack spacing={6} align="stretch">
        <Box bg={bgCard} border="1px solid" borderColor={borderColor} borderRadius="lg" p={5}>
          <VStack spacing={4} align="stretch">
            <Box>
              {editingGroup ? (
                <Text fontWeight="600" mb={2}>
                  Editing this notice
                </Text>
              ) : null}
              <FormLabel>Stores</FormLabel>
              <HStack w="full" justify="space-between" align="center" spacing={3}>
                <HStack spacing={3}>
                  <Button colorScheme="blue" variant={allSelected ? "solid" : "outline"} onClick={toggleAll}>
                    All
                  </Button>
                  <Button variant="outline" onClick={() => setPickerOpen(true)}>
                    {selectedStoreIds.length === 1 ? "1 store" : `${selectedStoreIds.length} stores`}
                  </Button>
                </HStack>
                {editingGroup ? (
                  <HStack spacing={3}>
                    <Button
                      variant="outline"
                      isLoading={busyId === editingKey}
                      onClick={() =>
                        setStatus(
                          editingGroup,
                          editingGroup.some((item) => item.status !== "disabled") ? "disabled" : "active"
                        )
                      }
                    >
                      {editingGroup.some((item) => item.status !== "disabled") ? "Turn off" : "Turn on"}
                    </Button>
                    <Button
                      colorScheme="red"
                      variant="outline"
                      isLoading={busyId === editingKey}
                      onClick={() => remove(editingGroup)}
                    >
                      Delete
                    </Button>
                  </HStack>
                ) : (
                  <Box />
                )}
              </HStack>
            </Box>

            <HStack spacing={2}>
              {(["fr", "en", "cn"] as LangId[]).map((lang) => (
                <Button
                  key={lang}
                  size="sm"
                  variant={langTab === lang ? "solid" : "ghost"}
                  colorScheme={langTab === lang ? "blue" : undefined}
                  onClick={() => setLangTab(lang)}
                >
                  {LANGS.find((item) => item.id === lang)?.label}
                </Button>
              ))}
            </HStack>
            <Box>
              <FormLabel>Title</FormLabel>
              <Input
                value={titles[langTab]}
                maxLength={TITLE_MAX}
                bg={bgCard}
                onChange={(event) => setTitles((current) => ({ ...current, [langTab]: event.target.value }))}
              />
            </Box>
            <HStack spacing={2}>
              <Button
                size="sm"
                variant={contentTab === "html" ? "solid" : "outline"}
                colorScheme={contentTab === "html" ? "blue" : undefined}
                onClick={() => setContentTab("html")}
              >
                HTML
              </Button>
              <Button
                size="sm"
                variant={contentTab === "text" ? "solid" : "outline"}
                colorScheme={contentTab === "text" ? "blue" : undefined}
                onClick={() => setContentTab("text")}
              >
                Plain text
              </Button>
            </HStack>
            <Box>
              <FormLabel>{contentTab === "html" ? "HTML" : "Message"}</FormLabel>
              <Textarea
                value={contentTab === "html" ? htmls[langTab] : bodies[langTab]}
                maxLength={BODY_MAX}
                rows={12}
                bg={bgCard}
                onChange={(event) => {
                  const value = event.target.value;
                  if (contentTab === "html") {
                    setHtmls((current) => ({ ...current, [langTab]: value }));
                    return;
                  }
                  setBodies((current) => ({ ...current, [langTab]: value }));
                }}
              />
              <Text mt={1} fontSize="sm" color={textSecondary} textAlign="right">
                {(contentTab === "html" ? htmls[langTab] : bodies[langTab]).length} / {BODY_MAX}
              </Text>
            </Box>

            <HStack w="full" justify="space-between" align="center" spacing={3}>
              {editingGroup ? (
                <Button variant="ghost" fontWeight="normal" onClick={clearComposer}>
                  Cancel
                </Button>
              ) : (
                <Box />
              )}
              <HStack spacing={3}>
                <Button
                  variant="outline"
                  fontWeight="normal"
                  isLoading={writeMode === "send"}
                  loadingText="Sending"
                  isDisabled={writeMode === "save"}
                  onClick={() => send(true)}
                >
                  Send notice
                </Button>
                <Button
                  variant="outline"
                  fontWeight="normal"
                  isLoading={writeMode === "save"}
                  loadingText="Saving"
                  isDisabled={writeMode === "send"}
                  onClick={() => send(false)}
                >
                  Save
                </Button>
              </HStack>
            </HStack>
          </VStack>
        </Box>

        <Box>
          <Text fontWeight="600" mb={3}>
            Notices
          </Text>
          {groups.length === 0 ? (
            <Text color={textSecondary}>No notices yet.</Text>
          ) : (
            <VStack spacing={3} align="stretch">
              {groups.map((group) => {
                const notice = group[0];
                const key = notice.batchId || notice.id;
                const state = group.some((item) => item.status === "active")
                  ? "Active"
                  : group.some((item) => item.status === "saved")
                    ? "Saved"
                    : "Off";
                return (
                  <Box key={key} bg={bgCard} border="1px solid" borderColor={borderColor} borderRadius="lg" p={4}>
                    <HStack justify="space-between" align="flex-start" spacing={4}>
                      <Box minW={0}>
                        <HStack spacing={2} mb={1}>
                          <Text fontWeight="600">{audienceOf(group)}</Text>
                          <Badge colorScheme={state === "Active" ? "green" : state === "Saved" ? "blue" : "gray"}>
                            {state}
                          </Badge>
                        </HStack>
                        <Text fontSize="sm" color={textSecondary}>
                          {languagesOf(notice).join(", ") || "No language"} · {whenOf(notice)}
                        </Text>
                        <Text mt={2} noOfLines={2}>
                          {previewOf(notice)}
                        </Text>
                      </Box>
                      <Button size="sm" variant="outline" flexShrink={0} onClick={() => beginEdit(group)}>
                        Edit
                      </Button>
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>
      </VStack>

      <Modal
        isOpen={pickerOpen}
        onClose={() => {
          setPickerOpen(false);
          setStoreSearch("");
        }}
        size="lg"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Stores</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              placeholder="Search stores"
              value={storeSearch}
              mb={3}
              onChange={(event) => setStoreSearch(event.target.value)}
            />
            <VStack align="stretch" spacing={0}>
              <Box px={3} py={3} borderBottom="1px solid" borderColor={borderColor}>
                <Checkbox
                  size="lg"
                  w="full"
                  flexDirection="row-reverse"
                  justifyContent="space-between"
                  isChecked={visibleAllSelected}
                  onChange={toggleVisible}
                >
                  <Text as="span" fontWeight="700">
                    {storeSearch.trim() ? "All matching" : "All"}
                  </Text>
                </Checkbox>
              </Box>
              {visibleStores.map((store) => (
                <Box key={store.id} px={3} py={3} borderBottom="1px solid" borderColor={borderColor}>
                  <Checkbox
                    size="lg"
                    w="full"
                    flexDirection="row-reverse"
                    justifyContent="space-between"
                    isChecked={selectedStoreIds.includes(store.id)}
                    onChange={() => toggleStore(store.id)}
                  >
                    {store.name || store.id}
                  </Checkbox>
                </Box>
              ))}
              {visibleStores.length === 0 && (
                <Text color={textSecondary} px={3} py={3}>
                  No stores match.
                </Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={() => {
                setPickerOpen(false);
                setStoreSearch("");
              }}
            >
              Done
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageShell>
  );
};

export default NoticeBroadcast;
